import {
  ANALYTICS_CONTACTS_AMOUNT,
  ANALYTICS_TOP_DIALOGUES,
  BOTS,
  CONTACTS_BY_TYPE,
  CONTACTS_GATHERED,
  CONTACTS_SOURCES_ANALYTICS,
  DEMOGRAPHIC_ANALYTICS,
  EXPORT_QA_KEYWORDS,
  EXPORT_UNANSWERED_QUESTIONS,
  QA_KEYWORDS,
  UNANSWERED_QUESTIONS,
  XRAY_ANALYTICS,
} from '@mm/api-routes';
import { ObjectUtils } from '@mm/utils';
import * as queryString from 'query-string';
import { snake } from '../helpers/converterString';
import {
  AnalyticsParams,
  ContactsGatheredResponseType,
  DemographicAnalyticsCategory,
  DemographicAnalyticsType,
  EmailAnalyticsParams,
  XRayAnalyticsType,
} from '../interfaces/botAnalytics';
import { MyLeadsAttributes } from '../interfaces/myLeads';

import AuthenticatedController from './AuthenticatedController';

export default class BotAnalyticsController extends AuthenticatedController {
  static async getTopDialogues(
    botId: number,
    pageNumber?: number,
    pageSize?: number,
    startDate?: string,
    endDate?: string,
  ) {
    return super.get(
      ANALYTICS_TOP_DIALOGUES(botId, pageNumber, pageSize, startDate, endDate),
    );
  }
  static async getContactsAmountAnalytics(
    botId: number,
    startDate?: string,
    endDate?: string,
  ) {
    return super.get(ANALYTICS_CONTACTS_AMOUNT(botId, startDate, endDate));
  }
  static async getUnansweredQuestions(
    botId: number,
    startDate?: string,
    endDate?: string,
  ) {
    return super.get(UNANSWERED_QUESTIONS(botId, startDate, endDate));
  }
  static async getContactsByType(
    type: 'by_source' | 'by_platform' | 'by_platform_aggregated',
    botId: number,
    startDate?: string,
    endDate?: string,
  ) {
    return super.get(CONTACTS_BY_TYPE(type, botId, startDate, endDate));
  }
  static async exportUnansweredQuestions(
    botId: number,
    email: string,
    startDate?: string,
    endDate?: string,
  ) {
    return super.post(EXPORT_UNANSWERED_QUESTIONS(botId), {
      email,
      startDate,
      endDate,
    });
  }
  static async getQAKeywords(
    botId: number,
    startDate?: string,
    endDate?: string,
  ) {
    return super.get(QA_KEYWORDS(botId, startDate, endDate));
  }

  static async exportQAKeywords(
    botId: number,
    email: string,
    startDate?: string,
    endDate?: string,
  ) {
    return super.post(EXPORT_QA_KEYWORDS(botId), {
      email,
      startDate,
      endDate,
    });
  }

  static getEmailAnalytics(params: EmailAnalyticsParams) {
    const { botId } = params;
    const query = queryString.stringify(snake(params));
    return super.get(`${BOTS}/${botId}/analytics/emails?${query}`);
  }

  static getXRayAnalytics(type: XRayAnalyticsType, params: AnalyticsParams) {
    const { botId, startDate, endDate, audiencesIds } = params;
    const audienceIdsStr = new URLSearchParams();
    audiencesIds?.forEach(id => {
      audienceIdsStr.append('audiences_ids[]', id.toString());
    });

    return super.get(
      XRAY_ANALYTICS(type, botId, startDate, endDate, audienceIdsStr),
    );
  }

  static getDemographicAnalytics(
    botId: number,
    category: DemographicAnalyticsCategory,
    query?: MyLeadsAttributes,
    audiencesIds?: number[],
  ): Promise<DemographicAnalyticsType> {
    const queryParams = new URLSearchParams();

    audiencesIds?.forEach(id => {
      queryParams.append('audiences_ids[]', id.toString());
    });
    if (category) {
      queryParams.append('category', category);
    }

    if (query) {
      queryParams.append(
        'query',
        JSON.stringify(ObjectUtils.mapObjKeysToSnakeCase(query || {})),
      );
    }

    return super.get(`${DEMOGRAPHIC_ANALYTICS(botId)}?${queryParams}`);
  }

  static getContactsSourcesAnalytics(botId: number) {
    return super.get(CONTACTS_SOURCES_ANALYTICS(botId));
  }

  static async getContactsGatheredAnalytics(
    companyId: number,
    startDate?: string,
    endDate?: string,
  ): Promise<ContactsGatheredResponseType> {
    return super.get(CONTACTS_GATHERED(companyId, startDate, endDate));
  }
}
